import React, { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";
import "./AdminDashboard.css";

const BAR_COLORS = ["#2563eb", "#0891b2", "#7c3aed", "#059669", "#d97706", "#dc2626"];

const TENT_LABELS = {
  "20x20_single": "20×20 Individual",
  "20x20_shared": "20×20 Shared",
  "10x10_single": "10×10 Individual",
  "food_court":   "Food Court",
  "pod_shared":   "17×17 Pod",
  "20x20_open":   "20×20 Open",
  "sponsored":    "Sponsored",
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="chart-tooltip">
        <p className="tooltip-label">{label}</p>
        <p className="tooltip-value">
          {payload[0].value} vendor{payload[0].value !== 1 ? "s" : ""}
        </p>
      </div>
    );
  }
  return null;
};

const EMPTY_STATS = {
  pending:                      0,
  approved:                     0,
  rejected:                     0,
  depositSubmittedUnconfirmed:  0,
  depositConfirmed:             0,
  balanceSubmittedUnconfirmed:  0,
  balanceConfirmed:             0,
  totalRevenue:                 0,
  tentBreakdown:                {},
};

const AdminDashboard = () => {
  const [events, setEvents]           = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [stats, setStats]             = useState(EMPTY_STATS);
  const [loading, setLoading]         = useState(false);
  const [animating, setAnimating]     = useState(false);

  // ── Fetch events ──
  useEffect(() => {
    const fetchEvents = async () => {
      const { data, error } = await supabase
        .from("events")
        .select("id, name, start_date")
        .order("start_date", { ascending: false });

      if (error) { console.error(error); setEventsLoading(false); return; }

      setEvents(data || []);
      const global = data?.find(ev => ev.name?.toLowerCase().includes("global"));
      setSelectedEvent(global?.id || data?.[0]?.id);
      setEventsLoading(false);
    };
    fetchEvents();
  }, []);

  // ── Fetch stats whenever event changes ──
  useEffect(() => {
    if (!selectedEvent) return;

    const fetchStats = async () => {
      setLoading(true);
      setAnimating(false);
      setStats(EMPTY_STATS);

      const { data, error } = await supabase
        .from("vendor_applications")
        .select(`
          id,
          status,
          tent_type,
          invoice_total,
          deposit_submitted,
          deposit_confirmed,
          balance_submitted,
          balance_confirmed,
          custom_base_price   
        `)
        .eq("event_id", selectedEvent);

      if (error) { console.error(error); setLoading(false); return; }

      const approved  = data.filter(a => a.status === "approved");
      const pending   = data.filter(a => a.status === "pending").length;
      const rejected  = data.filter(a => a.status === "denied").length;

      const depositSubmittedUnconfirmed = approved.filter(
        a => a.deposit_submitted && !a.deposit_confirmed
      ).length;

      const depositConfirmed = approved.filter(
        a => a.deposit_confirmed
      ).length;

      const balanceSubmittedUnconfirmed = approved.filter(
        a => a.balance_submitted && !a.balance_confirmed
      ).length;

      const balanceConfirmed = approved.filter(
        a => a.balance_confirmed
      ).length;

      let totalRevenue = 0;
      const tentBreakdown = {};

      approved.filter(a => a.balance_confirmed).forEach(app => {
        const key   = app.tent_type || "unknown";
        const label = TENT_LABELS[key] || key;
        const price = app.invoice_total ?? 0;
        totalRevenue += price;
        if (!tentBreakdown[label]) tentBreakdown[label] = { count: 0, total: 0 };
        tentBreakdown[label].count += 1;
        tentBreakdown[label].total += price;
      });

      setStats({
        pending,
        approved: approved.length,
        rejected,
        depositSubmittedUnconfirmed,
        depositConfirmed,
        balanceSubmittedUnconfirmed,
        balanceConfirmed,
        totalRevenue,
        tentBreakdown,
      });

      setLoading(false);
      setTimeout(() => setAnimating(true), 50);
    };

    fetchStats();
  }, [selectedEvent]);

  // ── Loading: events not yet fetched ──
  if (eventsLoading) return (
    <div className="adm-loading">
      <div className="adm-spinner" />
      <span>Loading dashboard…</span>
    </div>
  );

  const selectedEventName = events.find(e => e.id === selectedEvent)?.name || "";
  const chartData = Object.entries(stats.tentBreakdown).map(([name, d]) => ({ name, count: d.count }));
  const totalApproved = stats.approved;

  const paymentFunnel = [
    {
      label: "Approved",
      value: totalApproved,
      color: "#6b7280",
      sub: null,
    },
    {
      label: "Deposit Submitted",
      value: stats.depositSubmittedUnconfirmed + stats.depositConfirmed,
      color: "#d97706",
      sub: stats.depositSubmittedUnconfirmed > 0
        ? `${stats.depositSubmittedUnconfirmed} awaiting your confirmation`
        : null,
    },
    {
      label: "Deposit Confirmed",
      value: stats.depositConfirmed,
      color: "#2563eb",
      sub: null,
    },
    {
      label: "Balance Submitted",
      value: stats.balanceSubmittedUnconfirmed + stats.balanceConfirmed,
      color: "#7c3aed",
      sub: stats.balanceSubmittedUnconfirmed > 0
        ? `${stats.balanceSubmittedUnconfirmed} awaiting your confirmation`
        : null,
    },
    {
      label: "Fully Paid",
      value: stats.balanceConfirmed,
      color: "#059669",
      sub: null,
    },
  ];

  return (
    <div className={`adm-container ${animating ? "adm-loaded" : ""}`}>

      {/* ── HEADER ── */}
      <div className="adm-header" style={{ "--delay": "0ms" }}>
        <div>
          <h1 className="adm-title">Overview</h1>
          <p className="adm-subtitle">
            {selectedEventName} · Vendor Management
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {/* Event switcher */}
          {events.length > 0 && (
            <select
              style={{
                fontFamily: "Figtree, sans-serif",
                fontSize: "0.88rem",
                fontWeight: 600,
                padding: "9px 32px 9px 14px",
                border: "1px solid #e8eaed",
                borderRadius: "10px",
                background: "#fff",
                color: "#111827",
                outline: "none",
                cursor: "pointer",
                appearance: "none",
                WebkitAppearance: "none",
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 10px center",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
              value={selectedEvent || ""}
              onChange={e => setSelectedEvent(e.target.value)}
            >
              {events.map(ev => (
                <option key={ev.id} value={ev.id}>
                  {ev.name}{ev.start_date ? ` · ${new Date(ev.start_date).getFullYear()}` : ""}
                </option>
              ))}
            </select>
          )}

          <div className="adm-header-tag">
            <span className="adm-live-dot" />
            Live
          </div>
        </div>
      </div>

      {/* ── Loading overlay for event switch ── */}
      {loading ? (
        <div className="adm-loading" style={{ height: "40vh" }}>
          <div className="adm-spinner" />
          <span>Loading…</span>
        </div>
      ) : (
        <>
          {/* ── TOP KPI ROW ── */}
          <div className="adm-kpi-row">
            <div className="adm-kpi adm-kpi--blue" style={{ "--delay": "40ms" }}>
              <div className="adm-kpi-body">
                <span className="adm-kpi-value">{stats.approved}</span>
                <span className="adm-kpi-label">Total Approved</span>
              </div>
            </div>
            <div className="adm-kpi adm-kpi--green" style={{ "--delay": "80ms" }}>
              <div className="adm-kpi-body">
                <span className="adm-kpi-value">{stats.balanceConfirmed}</span>
                <span className="adm-kpi-label">Fully Paid</span>
              </div>
            </div>
            <div className="adm-kpi adm-kpi--amber" style={{ "--delay": "120ms" }}>
              <div className="adm-kpi-body">
                <span className="adm-kpi-value">{stats.pending}</span>
                <span className="adm-kpi-label">Pending Review</span>
              </div>
            </div>
            <div className="adm-kpi adm-kpi--red" style={{ "--delay": "160ms" }}>
              <div className="adm-kpi-body">
                <span className="adm-kpi-value">{stats.rejected}</span>
                <span className="adm-kpi-label">Rejected</span>
              </div>
            </div>
          </div>

          {/* ── REVENUE BANNER ── */}
          <div className="adm-revenue-banner" style={{ "--delay": "200ms" }}>
            <div className="adm-revenue-left">
              <span className="adm-revenue-label">Confirmed Revenue</span>
              <span className="adm-revenue-amount">
                KES {stats.totalRevenue.toLocaleString()}
              </span>
              <span className="adm-revenue-note">
                From {stats.balanceConfirmed} fully paid vendor{stats.balanceConfirmed !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="adm-revenue-right">
              <div className="adm-rev-group">
                <span className="adm-rev-group-label">Deposit</span>
                <div className="adm-rev-pills">
                  <div className={`adm-rev-pill ${stats.depositSubmittedUnconfirmed > 0 ? "adm-rev-pill--action" : ""}`}>
                    <span>Awaiting Confirm</span>
                    <strong>{stats.depositSubmittedUnconfirmed}</strong>
                  </div>
                  <div className="adm-rev-pill adm-rev-pill--done">
                    <span>Confirmed</span>
                    <strong>{stats.depositConfirmed}</strong>
                  </div>
                </div>
              </div>

              <div className="adm-rev-divider" />

              <div className="adm-rev-group">
                <span className="adm-rev-group-label">Balance</span>
                <div className="adm-rev-pills">
                  <div className={`adm-rev-pill ${stats.balanceSubmittedUnconfirmed > 0 ? "adm-rev-pill--action" : ""}`}>
                    <span>Awaiting Confirm</span>
                    <strong>{stats.balanceSubmittedUnconfirmed}</strong>
                  </div>
                  <div className="adm-rev-pill adm-rev-pill--done">
                    <span>Confirmed</span>
                    <strong>{stats.balanceConfirmed}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── MIDDLE ROW ── */}
          <div className="adm-mid-row">

            {/* Payment funnel */}
            <div className="adm-card" style={{ "--delay": "240ms" }}>
              <h3 className="adm-card-title">Payment Pipeline</h3>
              <div className="adm-funnel">
                {paymentFunnel.map((step, i) => {
                  const pct = totalApproved > 0
                    ? Math.round((step.value / totalApproved) * 100)
                    : 0;
                  return (
                    <div key={step.label} className="adm-funnel-row" style={{ "--i": i }}>
                      <div className="adm-funnel-meta">
                        <span className="adm-funnel-label" style={{ color: step.color }}>
                          {step.label}
                        </span>
                        <div className="adm-funnel-count-wrap">
                          <span className="adm-funnel-count">{step.value}</span>
                          {step.sub && (
                            <span className="adm-funnel-sub">↳ {step.sub}</span>
                          )}
                        </div>
                      </div>
                      <div className="adm-funnel-track">
                        <div
                          className="adm-funnel-fill"
                          style={{ width: `${pct}%`, background: step.color }}
                        />
                      </div>
                      <span className="adm-funnel-pct">{pct}%</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Tent breakdown */}
            <div className="adm-card" style={{ "--delay": "280ms" }}>
              <h3 className="adm-card-title">
                Tent Breakdown <span className="adm-card-note">fully paid</span>
              </h3>
              {Object.keys(stats.tentBreakdown).length > 0 ? (
                <div className="adm-tent-list">
                  {Object.entries(stats.tentBreakdown).map(([tent, { count, total }], i) => (
                    <div key={tent} className="adm-tent-row" style={{ "--i": i }}>
                      <div className="adm-tent-dot" style={{ background: BAR_COLORS[i % BAR_COLORS.length] }} />
                      <span className="adm-tent-name">{tent}</span>
                      <span className="adm-tent-count">{count}</span>
                      {total > 0 && (
                        <span className="adm-tent-revenue">KES {total.toLocaleString()}</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="adm-empty">No fully paid vendors yet.</p>
              )}
            </div>
          </div>

          {/* ── CHART ── */}
          <div className="adm-card adm-chart-card" style={{ "--delay": "320ms" }}>
            <h3 className="adm-card-title">
              Tent Type Distribution
              <span className="adm-card-note">fully paid vendors</span>
            </h3>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartData} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(37,99,235,0.05)" }} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={56}>
                    {chartData.map((_, i) => (
                      <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="adm-empty">No chart data available yet.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;